import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AgentContactInfosDto, AgentContactInfosUpdateDto } from '@workspace/data-types';
import { PhoneNumberInputComponent } from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

@Component({
  selector: 'etxe-agent-contact-infos-form',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTooltipModule,
    PhoneNumberInputComponent,
  ],
  templateUrl: './agent-contact-infos-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentContactInfosFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly userStore = inject(UserStore);

  readonly contactInfos = input<AgentContactInfosDto | undefined>(undefined);

  readonly defaultPhone = computed(
    () => this.userStore.currentUser()?.contactInfos?.mobileNumber?.trim() || null,
  );
  readonly defaultEmail = computed(() => this.userStore.currentUser()?.contactInfos?.email?.trim() || null);

  readonly validityChanged = output<boolean>();
  readonly dtoChanged = output<{ dto: AgentContactInfosUpdateDto; changed: boolean }>();

  readonly phoneControl = new FormControl<string | null>(null);
  readonly emailControl = new FormControl<string>('');
  readonly showPhone = signal<boolean>(true);
  readonly showEmail = signal<boolean>(true);

  readonly currentPhoneValue = signal<string | null>(null);
  readonly currentEmailValue = signal<string>('');

  readonly hasCustomPhone = computed(() => !!this.currentPhoneValue());
  readonly hasCustomEmail = computed(() => !!this.currentEmailValue()?.trim());

  private initialPhone: string | null = null;
  private initialEmail: string | null = null;
  private initialShowPhone = true;
  private initialShowEmail = true;

  constructor() {
    effect(() => {
      const ci = this.contactInfos();
      untracked(() => {
        const phoneVal = ci?.mobileNumber?.trim() || null;
        const emailVal = ci?.email?.trim() || '';
        const showPhoneVal = ci?.showMobileNumber ?? true;
        const showEmailVal = ci?.showEmail ?? true;

        this.initialPhone = phoneVal;
        this.initialEmail = emailVal;
        this.initialShowPhone = showPhoneVal;
        this.initialShowEmail = showEmailVal;

        this.phoneControl.setValue(phoneVal, { emitEvent: false });
        this.emailControl.setValue(emailVal, { emitEvent: false });
        this.currentPhoneValue.set(phoneVal);
        this.currentEmailValue.set(emailVal);
        this.showPhone.set(showPhoneVal);
        this.showEmail.set(showEmailVal);

        this.emitState();
      });
    });
  }

  ngOnInit(): void {
    this.phoneControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      this.currentPhoneValue.set(val?.trim() || null);
      this.emitState();
    });

    this.emailControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
      this.currentEmailValue.set(val?.trim() || '');
      this.emitState();
    });
  }

  toggleShowPhone(checked: boolean): void {
    this.showPhone.set(checked);
    this.emitState();
  }

  toggleShowEmail(checked: boolean): void {
    this.showEmail.set(checked);
    this.emitState();
  }

  onNumberChanged(val: string): void {
    const norm = val?.trim() || null;
    this.phoneControl.setValue(norm);
    this.currentPhoneValue.set(norm);
    this.emitState();
  }

  clearPhone(): void {
    this.phoneControl.setValue(null);
    this.currentPhoneValue.set(null);
  }

  clearEmail(): void {
    this.emailControl.setValue('');
    this.currentEmailValue.set('');
  }

  private isEmailValid(email: string): boolean {
    if (!email) return true;
    return EMAIL_REGEX.test(email);
  }

  private isPhoneValid(): boolean {
    const val = this.phoneControl.value;
    if (!val) return true;
    return this.phoneControl.valid;
  }

  private emitState(): void {
    const rawEmail = this.emailControl.value?.trim() || '';
    const rawPhone = this.phoneControl.value?.trim() || null;
    const currentShowPhone = this.showPhone();
    const currentShowEmail = this.showEmail();

    const emailValid = this.isEmailValid(rawEmail);
    const phoneValid = this.isPhoneValid();
    const isValid = emailValid && phoneValid;

    const currentEmailNorm = rawEmail || null;
    const initialEmailNorm = this.initialEmail || null;
    const currentPhoneNorm = rawPhone || null;
    const initialPhoneNorm = this.initialPhone || null;

    const changed =
      currentShowPhone !== this.initialShowPhone ||
      currentShowEmail !== this.initialShowEmail ||
      currentEmailNorm !== initialEmailNorm ||
      currentPhoneNorm !== initialPhoneNorm;

    const dto: AgentContactInfosUpdateDto = {
      contactInfos: {
        email: rawEmail || undefined,
        showEmail: currentShowEmail,
        mobileNumber: rawPhone || undefined,
        showMobileNumber: currentShowPhone,
      },
    };

    untracked(() => {
      this.validityChanged.emit(isValid);
      this.dtoChanged.emit({ dto, changed });
    });
  }
}
